<script setup lang="ts">

import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { CardAdmin } from '@shared/ui/cards'
import { BtnSecondary } from '@shared/ui/buttons'
import { AppBadge } from '@shared/ui/badges'

import {
  LESSON_PARTICIPANT_TITLES,
  LESSON_TIER_LABELS,
  formatConnectionLoss,
  getConnectionSummaryText,
} from '../../model/lessons/lesson-connection-log'
import { useLessonEditTitle } from '../../model/lessons/lesson-edit-title.hook'
import { useLessonConnectionLog } from '../../model/lessons/lesson-connection-log.hook'
import { LessonConnectionTier } from '../../model/lessons/enums'

defineOptions({
  name: 'LessonConnectionLog',
})

const COLUMNS: readonly string[] = ['Who', 'Connection', 'Time', 'Held for', 'Loss']

const {
  refIsLoading,
  refEntries,
  refSummaries,
  refIsEmpty,
  refLessonsUrl,
  formatTime,
} = useLessonConnectionLog()

const { refTitle } = useLessonEditTitle()
</script>

<template>
  <CardAdmin
    :title="refTitle"
    class="lesson-connection-log"
    fullwidth
  >
    <template #header>
      <div class="lesson-connection-log__controls">
        <BtnSecondary
          text="Go back"
          color="black"
          :to="refLessonsUrl"
        />
      </div>
    </template>
    <div class="lesson-connection-log__body">
      <AppText
        v-if="refIsEmpty"
        name="body1"
        color="slate-900"
      >
        No connection changes were logged for this lesson.
      </AppText>
      <template v-else>
        <ul class="lesson-connection-log__summary">
          <li
            v-for="summary in refSummaries"
            :key="summary.participant"
            class="lesson-connection-log__summary-item"
          >
            <AppText
              name="body2"
              color="slate-900"
            >
              {{ getConnectionSummaryText(summary) }}
            </AppText>
            <AppBadge
              v-if="summary.worstTier !== LessonConnectionTier.Good"
              :color="LESSON_TIER_LABELS[summary.worstTier].color"
              :text="LESSON_TIER_LABELS[summary.worstTier].text"
              :prepend-icon="LESSON_TIER_LABELS[summary.worstTier].icon"
            />
          </li>
        </ul>
        <AppTable
          class="rounded-table"
          :loading="refIsLoading"
          :paginated="false"
        >
          <template #head>
            <AppTr>
              <AppTh
                v-for="column in COLUMNS"
                :key="column"
              >
                <AppText
                  name="body1-medium"
                  color="slate-900"
                >
                  {{ column }}
                </AppText>
              </AppTh>
            </AppTr>
          </template>
          <template
            v-if="refEntries.length"
            #body
          >
            <AppTr
              v-for="entry in refEntries"
              :key="entry.id"
            >
              <AppTd class="medium">
                <AppText name="body1">
                  {{ LESSON_PARTICIPANT_TITLES[entry.participant_type] }}
                </AppText>
              </AppTd>
              <AppTd class="medium">
                <AppBadge
                  :color="LESSON_TIER_LABELS[entry.tier].color"
                  :text="LESSON_TIER_LABELS[entry.tier].text"
                  :prepend-icon="LESSON_TIER_LABELS[entry.tier].icon"
                />
              </AppTd>
              <AppTd class="medium">
                <AppText name="body1">
                  {{ formatTime(entry.created_at) }}
                </AppText>
              </AppTd>
              <AppTd class="medium">
                <AppText name="body1">
                  {{ entry.heldFor ?? '—' }}
                </AppText>
              </AppTd>
              <AppTd class="medium">
                <AppText name="body1">
                  {{ formatConnectionLoss(entry.loss_pct) }}
                </AppText>
              </AppTd>
            </AppTr>
          </template>
        </AppTable>
      </template>
    </div>
  </CardAdmin>
</template>

<style scoped lang="scss" src="./LessonConnectionLog.scss" />
